import React, { useState, ChangeEvent, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiClient } from '../../config/ApiClient';
import { UserDTO } from '../../dto/UserDTO';
import { RegisterDTO } from '../../dto/RegisterDTO';
import { showSuccess, showError } from '../../components/Alert';
import '../../styles/SignupForm.css';

const SignupForm: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    userName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Password match validation
    if (formData.password !== formData.confirmPassword) {
      showError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    // Build the payload for registerUser
    const userDto: UserDTO = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      userName: formData.userName,
      email: formData.email,
      addresses: [],
    };

    const payload: RegisterDTO = {
      password: formData.password,
      userDto,
    };

    try {
      await apiClient.post('/auth/registerUser', payload);
      showSuccess('Account created successfully!');
      navigate('/login');
    } catch (err: any) {
      if (err.response) {
        if (err.response.status >= 500) {
          showError('Internal server error. Please try again later.');
        } else {
          // Extract backend message
          const data = err.response.data;
          const msg =
            typeof data === 'string'
              ? data
              : data?.message || JSON.stringify(data) || err.message;
          showError(msg);
        }
      } else {
        showError(err.message || 'Registration failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="px-4 py-3">
      <form onSubmit={handleSubmit}>
        <div className="row g-3">
          {/* First & Last Name */}
          <div className="col-md-6">
            <div className="form-floating">
              <input
                id="firstName"
                type="text"
                className="form-control input-box"
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleChange}
                required
                disabled={isLoading}
              />
              <label htmlFor="firstName">First Name</label>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-floating">
              <input
                id="lastName"
                type="text"
                className="form-control input-box"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                required
                disabled={isLoading}
              />
              <label htmlFor="lastName">Last Name</label>
            </div>
          </div>

          {/* Username & Email */}
          <div className="col-12">
            <div className="form-floating">
              <input
                id="userName"
                type="text"
                className="form-control input-box"
                placeholder="Username"
                value={formData.userName}
                onChange={handleChange}
                required
                disabled={isLoading}
              />
              <label htmlFor="userName">Username</label>
            </div>
          </div>
          <div className="col-12">
            <div className="form-floating">
              <input
                id="email"
                type="email"
                className="form-control input-box"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={isLoading}
              />
              <label htmlFor="email">Email Address</label>
            </div>
          </div>

          {/* Password Fields */}
          <div className="col-md-6">
            <div className="form-floating">
              <input
                id="password"
                type="password"
                className="form-control input-box"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                required
                disabled={isLoading}
              />
              <label htmlFor="password">Password</label>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-floating">
              <input
                id="confirmPassword"
                type="password"
                className="form-control input-box"
                placeholder="Confirm Password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                disabled={isLoading}
              />
              <label htmlFor="confirmPassword">Confirm Password</label>
            </div>
          </div>

          {/* Submit */}
          <div className="col-12 mt-4 text-center">
            <button
              type="submit"
              className="btn btn-lg w-100 py-3 btn-submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="spinner-border spinner-border-sm" />
              ) : (
                'Sign Up'
              )}
            </button>
          </div>
          <div className="col-12 text-center mt-3">
            <p className="account-prompt">
              Already have an account?{' '}
              <Link to="/login" className="link">
                Log In
              </Link>
            </p>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SignupForm;
